import React, { useEffect, useMemo, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import { useUserData } from "../../../hooks/useUserData";
import {
  parsePermissions,
  hasPermission,
  getAllPermissionKeys,
  isSystemAdministratorRole,
} from "../../../utils/permissionUtils";
import { organizationHasIsfModule } from "../../../utils/orgModuleAccess";
import EisfDocumentTypes from "../isf/EisfDocumentTypes";
import EisfDocumentCategories from "../isf/EisfDocumentCategories";
import "./CapaConfigMaster.scss";

const CONFIG_SECTIONS = [
  {
    key: "documentTypes",
    label: "Document Type",
    subtitle: "eISF document type master values",
    icon: "fas fa-tag",
    permissionKeys: ["EISF_CONFIGURE"],
  },
  {
    key: "documentCategories",
    label: "Document Category",
    subtitle: "eISF document category master values",
    icon: "fas fa-folder-tree",
    permissionKeys: ["EISF_CONFIGURE"],
  },
];

function sectionVisible(section, userPermissions, isSystemAdmin) {
  if (isSystemAdmin) return true;
  const keys = section.permissionKeys || ["EISF_CONFIGURE"];
  return keys.some((key) => hasPermission(userPermissions, key));
}

const EisfConfigMaster = () => {
  const { userData, loading } = useUserData();
  const [searchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState("documentTypes");
  const isSystemAdmin = isSystemAdministratorRole(userData?.Role);

  const userPermissions = isSystemAdmin
    ? getAllPermissionKeys()
    : parsePermissions(userData?.Permission || "");

  const visibleSections = useMemo(
    () => CONFIG_SECTIONS.filter((section) => sectionVisible(section, userPermissions, isSystemAdmin)),
    [userPermissions, isSystemAdmin],
  );

  const canAccess = useMemo(() => {
    if (loading || !userData) return false;
    if (isSystemAdmin) return true;
    if (!organizationHasIsfModule(userData)) return false;
    return hasPermission(userPermissions, "EISF_CONFIGURE");
  }, [userData, loading, isSystemAdmin, userPermissions]);

  useEffect(() => {
    const section = searchParams.get("section");
    if (section && visibleSections.some((item) => item.key === section)) {
      setActiveSection(section);
    }
  }, [searchParams, visibleSections]);

  useEffect(() => {
    if (
      visibleSections.length > 0 &&
      !visibleSections.some((section) => section.key === activeSection)
    ) {
      setActiveSection(visibleSections[0].key);
    }
  }, [visibleSections, activeSection]);

  if (loading || !userData) {
    return (
      <div className="d-flex justify-content-center p-5">
        <div className="spinner-border" role="status" />
      </div>
    );
  }

  if (!canAccess) return <Navigate to="/notification-center" replace />;

  return (
    <div className="user-mgmt-page capa-config-saas">
      <PageBreadcrumb
        items={[
          { label: "Home", path: "/notification-center" },
          { label: "eISF Config" },
        ]}
      />

      <div className="row g-3 capa-config-saas__layout">
        <div className="col-md-4 col-xl-3 capa-config-saas__nav-col">
          <div className="card capa-config-saas__nav-card">
            <div className="card-header fw-semibold">Configuration sections</div>
            <div className="list-group list-group-flush capa-config-saas__nav-list">
              {visibleSections.map((section) => (
                <button
                  key={section.key}
                  type="button"
                  className={`list-group-item list-group-item-action capa-config-saas__nav-item ${
                    activeSection === section.key ? "active" : ""
                  }`}
                  onClick={() => setActiveSection(section.key)}
                  aria-current={activeSection === section.key ? "page" : undefined}
                >
                  <div className="d-flex align-items-start gap-2">
                    <i className={`${section.icon} mt-1`} aria-hidden="true" />
                    <div>
                      <div className="fw-semibold capa-config-saas__nav-item-title">
                        {section.label}
                      </div>
                      <div
                        className={`small capa-config-saas__nav-item-subtitle ${
                          activeSection === section.key ? "text-white-50" : "text-muted"
                        }`}
                      >
                        {section.subtitle}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="col-md-8 col-xl-9 capa-config-saas__content-col">
          {activeSection === "documentTypes" ? (
            <div className="card shadow-sm capa-config-saas__panel-card">
              <div className="card-body">
                <EisfDocumentTypes embedded />
              </div>
            </div>
          ) : null}

          {activeSection === "documentCategories" ? (
            <div className="card shadow-sm capa-config-saas__panel-card">
              <div className="card-body">
                <EisfDocumentCategories embedded />
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EisfConfigMaster;
